import {
    ComponentRef,
    EnvironmentInjector,
    inject,
    Injectable,
    Injector,
    OnDestroy,
    Type,
    ViewContainerRef
} from '@angular/core';
import {Observable} from 'rxjs';
import {DynamicComponentConfig, DynamicComponentRef} from './dynamic-component-ref';

interface DynamicComponentAbsolutePlaceOptions {
    viewContainerRef: ViewContainerRef;
    index?: number;
}

interface DynamicComponentPlaceAfterOptions {
    after: DynamicComponentRef<unknown>;
}

interface DynamicComponentPlaceBeforeOptions {
    before: DynamicComponentRef<unknown>;
}

export type DynamicComponentConfigurationCallback<C> =
    (instance: C, componentRef: ComponentRef<C>) => void;

export type DynamicComponentPlaceOptions =
    | DynamicComponentAbsolutePlaceOptions
    | DynamicComponentPlaceAfterOptions
    | DynamicComponentPlaceBeforeOptions;

export interface DynamicComponentCreationOptions<C> {
    /** DI parent of the created component. Defaults to the service context. */
    injector?: Injector;
    /** Environment tree of the created component. Defaults to the service context. */
    environmentInjector?: EnvironmentInjector;
    place?: DynamicComponentPlaceOptions;
    destroyWhen?: Observable<unknown>;
    configure?: DynamicComponentConfig<C> | DynamicComponentConfigurationCallback<C>;
    onDestroy?: () => void;
}

@Injectable()
export class DynamicComponentFactoryService implements OnDestroy {

    protected readonly injector: Injector = inject(Injector);
    protected readonly environmentInjector: EnvironmentInjector = inject(EnvironmentInjector);

    /** Components created through the service, in creation order */
    protected readonly list: DynamicComponentRef<unknown>[] = [];

    public ngOnDestroy(): void {
        this.destroyAll();
    }

    public create<C>(
        componentClass: Type<C>,
        options?: DynamicComponentCreationOptions<C>
    ): DynamicComponentRef<C> {
        const ref: DynamicComponentRef<C> = new DynamicComponentRef(
            componentClass,
            options?.injector ?? this.injector,
            options?.environmentInjector ?? this.environmentInjector
        )
            .onDestroy(() => this.removeFromList(ref))
            .createComponent();

        if (options) {
            // typeof narrowing splits the union across overloads, no cast "as any" needed
            if (typeof options.configure === 'function') {
                ref.configureComponent(options.configure);
            } else if (options.configure) {
                ref.configureComponent(options.configure);
            }

            if (options.onDestroy) {
                ref.onDestroy(options.onDestroy);
            }

            if (options.destroyWhen) {
                ref.destroyWhen(options.destroyWhen);
            }

            this.applyPlaceOptions(ref, options.place);
        }

        this.list.push(ref);

        return ref;
    }

    public destroyByInstance<C>(componentInstance: C): void {
        this.getByInstance(componentInstance)?.destroy();
    }

    /** LIFO: destroys in reverse creation order */
    public destroyAll(): void {
        const refs = [...this.list]; // snapshot: destroy() removes items from list
        for (let i = refs.length - 1; i >= 0; i--) {
            refs[i].destroy();
        }
    }

    public getByInstance<C>(componentInstance: C): DynamicComponentRef<C> | undefined {
        // unchecked downcast: C cannot be structurally restored from unknown
        return this.list.find(ref => ref.instance === componentInstance) as DynamicComponentRef<C> | undefined;
    }

    /**
     * Updates the configuration of an already created component.
     *
     * Delegates to configureComponent(): values for input properties are applied
     * via ComponentRef.setInput() (proper SimpleChanges with a real firstChange,
     * signal input() support, view marking and change detection scheduling —
     * important for zoneless); functions for output properties are subscribed to.
     *
     * Repeated calls: input values are overwritten; an output handler for a key
     * REPLACES the previous one (the old subscription is unsubscribed). Handlers
     * for keys not present in the config remain. All output subscriptions are
     * removed on destroy().
     *
     * Input keys must be declared as inputs (@Input()/input()). For regular
     * instance fields use the callback form of configureComponent().
     */
    public updateConfig<C>(ref: DynamicComponentRef<C>, config: DynamicComponentConfig<C>): void {
        ref.configureComponent(config);
    }

    protected applyPlaceOptions<C>(
        dynamicComponentRef: DynamicComponentRef<C>,
        options: DynamicComponentPlaceOptions | undefined
    ): void {
        if (!options) {
            return;
        }

        if ('viewContainerRef' in options) {
            dynamicComponentRef.insertTo(options.viewContainerRef, options.index);
        } else if ('after' in options) {
            dynamicComponentRef.insertAfter(options.after);
        } else if ('before' in options) {
            dynamicComponentRef.insertBefore(options.before);
        }
    }

    protected removeFromList(dynamicComponentRef: DynamicComponentRef<unknown>): void {
        const index: number = this.list.indexOf(dynamicComponentRef);

        if (index >= 0) {
            this.list.splice(index, 1);
        }
    }

}
